import {Icon, Loader} from '@atoms';
import {useAppDispatch, useAppSelector, useDialog, useModal} from '@hooks';
import {trackEvent} from '@lib/infrastructure';
import {
  MeetingInviteeSearchResult,
  ValidationError,
  clearDesks,
  getBuildingEntities,
  getIsLastNodeSelected,
  getNodeTypeAndId,
  getSelectedNodes,
  getSelectedTimeslot,
  getUser,
  resetSuggestedDesk,
  resetWorkspaceReservation,
  selectLocationFromBuildingId,
  useOverridableFeatureFlags,
  withAsyncThunkErrorHandling,
} from '@lib/store';
import {getConfirmedWorkspaceReservationsForWorkday} from '@lib/store/src/data/timeslots/selectors';
import {getScheduleForDateIsUpdating} from '@lib/store/src/data/timeslots/selectors/index';
import {actions} from '@lib/store/src/data/timeslots/actions';
import {
  isOfficeWorkdayDraft,
  OfficeWorkday,
  OfficeWorkdayDraft,
  WorkspaceReservation,
} from '@lib/store/src/data/timeslots/apiTypes';
import {dateKeyForDateTimeString, timeLabel} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {Attendee} from '@lib/store/src/data/timeslots/selectors/getAttendeeOverview';
import {getBuildingIdsThatRequireWorkspaceBooking} from '@lib/store/src/data/timeslots/selectors/getBuildingSections';
import {useWorkspaceHasSpaceForGroupBooking} from '@lib/store/src/ui/hooks/timeslots/useWorkspaceHasSpaceForGroupBooking';
import {ModalCell} from '@molecules';
import {
  AddWorkspaceInviteesCard,
  BookingBuildingSelectionCard,
  BookWorkspaceValidationError,
  ModalPageBottom,
  SelectInviteesButton,
  SelectWorkspaceButton,
  WorkspaceOnMapSection,
} from '@organisms';
import {Div, FlexCol, H4} from '@quarks';
import {isPlainObject} from '@reduxjs/toolkit';
import {generateTestIdCoreString} from '@utils';
import {useCallback, useEffect, useMemo, useState} from 'react';
import toast from 'react-hot-toast';
import {useTranslation} from 'react-i18next';
import {useTheme} from 'styled-components';
import {EditTimeModal} from './EditTimeModal';
import {reservationStateEqual, reservationStateFromExisting, reservationStateFromUI} from './ReservationState';
import {StyledFlexCol, StyledModalPage} from './styles';
import {ErrorsData} from './types';
import {useEnsureLastBookedWorkspaceData} from '@lib/logic';
import {WorkspaceAvailabilityWarning} from 'src/components/molecules/WorkspaceAvailabilityWarning';
import {ensureNoParkingSpotOnDate} from '@lib/store/src/data/parkingReservation/ensureNoParkingSpotOnDate';
import {WorkdayPatch} from '@lib/store/src/data/timeslots/trimming';
import {useCleanupWorkday} from './hooks/useCleanupWorkday';

type BookWorkspaceCardProps = {
  workday: OfficeWorkday | OfficeWorkdayDraft;
  workspaceReservation?: WorkspaceReservation;
  members?: Array<Attendee>;
};

// This wrapper ensures we have a sensible last selection
export const BookWorkspaceCard = (props: BookWorkspaceCardProps) => {
  const isLoadingLastWorkspaceData = useEnsureLastBookedWorkspaceData({
    workday: props.workday,
    workspaceReservation: props.workspaceReservation,
  });

  if (isLoadingLastWorkspaceData) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader />
      </div>
    );
  }

  return <_BookWorkspaceCard {...props} />;
};

const _BookWorkspaceCard = ({workday, workspaceReservation, members = []}: BookWorkspaceCardProps) => {
  const features = useOverridableFeatureFlags();
  const date = dateKeyForDateTimeString(workday.localStart);
  const TEST_ID_CORE = generateTestIdCoreString(import.meta.url);

  const dispatch = useAppDispatch();
  const theme = useTheme();
  const {t} = useTranslation();
  const {destructiveDialog} = useDialog();

  const mutating = useAppSelector(getScheduleForDateIsUpdating(dateKeyForDateTimeString(workday.localStart)));

  const selectedTimeslot = useAppSelector(getSelectedTimeslot);

  const [workdayPatch, setWorkdayPatch] = useState<WorkdayPatch | null>(null);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const {closeModal, addPages, removePages, modalPages, setOnCloseCallback} = useModal();

  const {id: userId} = useAppSelector(getUser);

  //// LOCATION
  const buildingEntities = useAppSelector(getBuildingEntities);
  const {buildingId, floorId, areaId, deskIds} = useAppSelector(getSelectedNodes);
  const buildingIdsWithRequiredWorkspaceBooking = useAppSelector(getBuildingIdsThatRequireWorkspaceBooking);
  const workdayRequiresWorkspaceBooking = buildingIdsWithRequiredWorkspaceBooking.has(workday.buildingId);
  const currentWorkdayConfirmedWorkspaceReservations = useAppSelector(
    getConfirmedWorkspaceReservationsForWorkday(workday.id),
  );
  const currentWorkdayHasConfirmedWorkspaceReservations = currentWorkdayConfirmedWorkspaceReservations.length > 0;

  //// PEOPLE
  // TODO: why do we not use the workspaceReservation ui store for this? There is an inviteesSelected...
  const initialInvitees = members.filter((m) => !m.isCurrentUser).map((m) => m.owner);
  const [invitees, setInvitees] = useState<MeetingInviteeSearchResult[]>(initialInvitees);

  // Time
  const [timespan, setTimespan] = useState(
    workspaceReservation
      ? {
          localStart: workspaceReservation.localStart,
          localEnd: workspaceReservation.localEnd,
        }
      : features.TimeslotsUI && selectedTimeslot
      ? {
          localStart: selectedTimeslot.startTime,
          localEnd: selectedTimeslot.endTime,
        }
      : {
          localStart: workday.localStart,
          localEnd: workday.localEnd,
        },
  );

  const {nodeIds, nodeType} = useMemo(() => {
    return getNodeTypeAndId(buildingId, floorId, areaId, deskIds);
  }, [areaId, buildingId, deskIds, floorId]);

  // Check validity of selections for saving
  const savedReservation = reservationStateFromExisting(members);
  const uiReservation = reservationStateFromUI(
    userId,
    invitees.map((inv) => inv.id),
    nodeIds,
  );
  const hasChanges =
    !reservationStateEqual(savedReservation, uiReservation) ||
    timespan.localStart !== workspaceReservation?.localStart ||
    timespan.localEnd !== workspaceReservation?.localEnd;

  // TODO: do various checks whether location is bookable etc.
  //      -> replace getCanSaveWorkspaceReservation

  const hasSpaceForGroupBooking = useWorkspaceHasSpaceForGroupBooking({
    workday: {
      ...workday,
      localStart: timespan.localStart,
      localEnd: timespan.localEnd,
    },
    buildingId,
    floorId,
    areaId,
    workspaceReservation,
    groupSizeIncludingOrganizer: invitees.length + 1,
  });

  const locationIsValid = hasSpaceForGroupBooking;
  const hasBookableSelection = useAppSelector(getIsLastNodeSelected);
  const canSubmit = hasBookableSelection && hasChanges && locationIsValid;

  // TODO: move to setInvitees
  useEffect(
    function clearErrorMessages() {
      setErrorMessages([]);
    },
    [invitees, nodeIds],
  );

  useEffect(() => {
    return () => {
      dispatch(resetWorkspaceReservation());
      dispatch(clearDesks());
      dispatch(resetSuggestedDesk());
    };
  }, [dispatch]);

  const handleOnSuccess = () => {
    const isGroupBooking = !!invitees.length;
    const isEdit = workspaceReservation !== undefined;
    const getFeedbackMessage = () => {
      if (isGroupBooking && isEdit) {
        return t('workplace:WorkplaceUpdatedMessage');
      }
      if (isGroupBooking) {
        return t('workplace:GroupBookingSent');
      }
      if (nodeType === 'Desk') {
        return t('workplace:DeskBookedMessage');
      }
      if (isEdit) {
        return t('workplace:WorkplaceUpdatedMessage');
      }
      return t('workplace:WorkplaceBookedMessage');
    };
    toast.success(getFeedbackMessage());
    closeModal();

    trackEvent(
      isEdit ? 'CalendarOverview_WorkplaceBooking__UpdateBooking' : 'CalendarOverview_WorkplaceBooking__CreateBooking',
      {
        date,
        isGroupBooking: isEdit ? members.length > 0 : isGroupBooking,
      },
    );
  };

  // Ensure the workday exists and isn't a draft
  const ensureWorkdayExists = useCallback(async () => {
    if (!isOfficeWorkdayDraft(workday)) return workday;

    const {result, success} = await dispatch(
      withAsyncThunkErrorHandling(() =>
        actions.createWorkday({
          status: 'OfficeDay',
          buildingId: workday.buildingId,
          localStart: timespan.localStart, // Start of day
          localEnd: timespan.localEnd, // End of day
        }),
      ),
    );

    if (!success) {
      throw new Error(t('timeslots:SomethingWentWrongCreatingWorkday'));
    }

    return {...workday, id: result.id};
  }, [workday, dispatch, timespan.localStart, timespan.localEnd, t]);

  const confirmCancellation = useCallback(
    () =>
      destructiveDialog(
        t('workplace:DeleteYourBookings'),
        t('workplace:DeleteYourBookingsMessage', {
          buildingName: workday?.status === 'OfficeDay' ? workday.buildingName : undefined,
        }),
        'reportProblem',
      ),
    [t, workday, destructiveDialog],
  );

  // If the building has changed and there are confirmed workspace reservations, we also handle cancellation
  const updateWorkdayBuildingIfNeeded = useCallback(async () => {
    if (workday.buildingId === buildingId) return true;

    if (currentWorkdayHasConfirmedWorkspaceReservations) {
      const confirmed = await confirmCancellation();
      if (!confirmed) return false;

      await dispatch(actions.cancelWorkspaceReservationsOnWorkday({workday}));
    }

    // Handle parking cancellation if needed
    await dispatch(ensureNoParkingSpotOnDate(dateKeyForDateTimeString(workday.localStart))).unwrap();

    // Then, connect workday to the new building
    if (!buildingId) {
      console.warn('Missing buildingId for updating workday.');
      return true;
    }

    await dispatch(
      withAsyncThunkErrorHandling(() =>
        actions.updateWorkday({
          ...workday,
          buildingId,
        }),
      ),
    );

    return true;
  }, [dispatch, buildingId, workday, currentWorkdayHasConfirmedWorkspaceReservations, confirmCancellation]);

  const saveReservation = async () => {
    try {
      const wd = await ensureWorkdayExists();

      // Apply any pending workday patches first
      if (workdayPatch) {
        await dispatch(
          actions.updateWorkday({
            ...workday,
            ...workdayPatch,
          }),
        );

        // Patch complete, clear state
        setWorkdayPatch(null);
      }

      const canProceed = await updateWorkdayBuildingIfNeeded();
      if (!canProceed) return;

      const [nodeIdForUser, ...forInvitees] = nodeIds;
      const base = {
        localStart: timespan.localStart,
        localEnd: timespan.localEnd,
        nodeId: nodeIdForUser,
        invitations: invitees.map((inv, idx) => {
          // Assign a required `nodeId` to each invitee:
          // - Be sure to use unique desk IDs for desk bookings
          // - Otherwise, we can use the user's nodeId (e.g. the area)
          const nodeId = nodeType === 'Desk' ? forInvitees[idx] : nodeIdForUser;

          return {
            nodeId,
            ownerId: inv.id,
          };
        }),
      };

      // // Note the unwrap; it is required for the error to be thrown
      if (!workspaceReservation) {
        await dispatch(
          actions.createWorkspaceReservation({
            ...base,
            workdayId: wd.id,
          }),
        ).unwrap();
      } else {
        await dispatch(
          actions.saveWorkspaceReservation({
            workspaceReservation,
            patch: base,
          }),
        ).unwrap();
      }

      handleOnSuccess();
    } catch (error: unknown) {
      if (error instanceof ValidationError) {
        const errorValidationMessages = extractErrorMessages(error.data.errors);
        setErrorMessages(errorValidationMessages);
      } else if (error instanceof Error) {
        setErrorMessages([error.message]);
      } else if (isPlainObject(error) && 'message' in error && typeof error.message === 'string') {
        setErrorMessages([error.message]);
      } else {
        setErrorMessages([t('workplace:SomethingWentWrong')]);
        console.error(error);
      }
    }
  };

  const {cleanUpWorkdayIfRequired} = useCleanupWorkday({
    workday,
    workspaceReservation,
  });

  // Modal controls
  const onBack =
    modalPages.length > 1
      ? () => {
          cleanUpWorkdayIfRequired();
          removePages(1);
        }
      : undefined;

  const onClose = useCallback(() => {
    cleanUpWorkdayIfRequired();
    closeModal();
  }, [cleanUpWorkdayIfRequired, closeModal]);

  // Override onClose callback to include workday cleanup
  useEffect(() => {
    setOnCloseCallback(() => () => onClose());

    return () => {
      setOnCloseCallback(false);
    };
    // Prevent infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setOnCloseCallback]);

  const openTimePicker = () => {
    addPages([
      <EditTimeModal
        defaultTimespan={timespan}
        onCancel={() => removePages(1)}
        onConfirm={async (localStart, localEnd, patch) => {
          if (patch) {
            setWorkdayPatch(patch);
          }

          setTimespan({localStart, localEnd});
          removePages(1);
        }}
        workspaceReservation={workspaceReservation}
        workday={workday}
      />,
    ]);
  };

  return (
    <StyledModalPage
      title={t('workplace:BookAWorkspace')}
      bottom={
        <ModalPageBottom
          buttonDisabled={!canSubmit || mutating}
          buttonLabel={workspaceReservation ? t('workplace:Update') : t('translation:Book')}
          buttonLoading={mutating}
          onClick={saveReservation}
        />
      }
      onBack={onBack}
      onClose={onClose}>
      <StyledFlexCol gap={8}>
        <Div>
          <FlexCol>
            {errorMessages.map((error, index) => (
              <Div
                key={`error-${index}`}
                background={theme.card.white.background}
                paddingLeft={16}
                paddingTop={16}
                paddingRight={16}>
                <BookWorkspaceValidationError text={error} />
              </Div>
            ))}
          </FlexCol>
          <FlexCol>
            <ModalCell>
              <H4
                as="h2"
                data-testid={`${TEST_ID_CORE}-modal-h2-invitees`}>
                {t('Invitees')}
              </H4>
            </ModalCell>
            <SelectInviteesButton
              placeholder={t('workplace:InviteesPlaceholder')}
              invitees={invitees}
              date={date}
              onClick={() =>
                addPages([
                  <AddWorkspaceInviteesCard
                    passedInvitees={invitees}
                    onSave={setInvitees}
                    onClose={closeModal}
                  />,
                ])
              }></SelectInviteesButton>
          </FlexCol>
        </Div>
        <FlexCol>
          <ModalCell>
            <H4
              as="h2"
              data-testid={`${TEST_ID_CORE}-modal-h2-Desk`}>
              {t('workplace:Workspace')}
            </H4>
          </ModalCell>

          {features.TimeslotsUI && (
            <WorkspaceAvailabilityWarning
              workday={workday}
              workspace={{
                buildingId,
              }}
              localStart={timespan.localStart}
              localEnd={timespan.localEnd}
              isEditing={!!workspaceReservation}
            />
          )}

          {features.TimeslotsUI && (
            <ModalCell>
              <div
                className="flex py-4 cursor-pointer hover:bg-mapiq-black-75 p-2 rounded-lg pr-1"
                onClick={() =>
                  addPages([
                    <BookingBuildingSelectionCard
                      date={dateKeyForDateTimeString(workday.localStart)}
                      onBack={() => removePages(1)}
                      onSelect={(building) => {
                        dispatch(selectLocationFromBuildingId(building.id));
                        removePages(1);
                      }}
                    />,
                  ])
                }>
                <span className="mr-auto">Building</span>

                <div className="text-mapiq-black-500 flex items-center gap-2">
                  <span>{buildingEntities[buildingId]?.name}</span>
                  <Icon
                    icon="caretRight"
                    size="24px"
                    className="text-mapiq-black-500 mt-[-3px]" /* Magic alignment... */
                  />
                </div>
              </div>
            </ModalCell>
          )}

          {features.TimeslotsUI && (
            <ModalCell>
              <div
                className="flex py-4 cursor-pointer hover:bg-mapiq-black-75 p-2 rounded-lg pr-1"
                onClick={openTimePicker}>
                <span className="mr-auto">Time</span>

                <div className="text-mapiq-black-500 flex items-center gap-2">
                  <span>{timeLabel(timespan.localStart, timespan.localEnd, t)}</span>
                  <Icon
                    icon="caretRight"
                    size="24px"
                    className="text-mapiq-black-500 mt-[-3px]" /* Magic alignment... */
                  />
                </div>
              </div>
            </ModalCell>
          )}

          <WorkspaceOnMapSection
            invitees={invitees}
            date={date}
            workday={workday}
            workspaceReservation={workspaceReservation}
            timespan={timespan}
          />

          <SelectWorkspaceButton
            workday={workday}
            workspaceReservation={workspaceReservation}
            attendees={members}
            inviteeCount={invitees.length + 1}
            timespan={timespan}
          />
        </FlexCol>
      </StyledFlexCol>
    </StyledModalPage>
  );
};

function extractErrorMessages(errors: ErrorsData): string[] {
  return Object.values(errors).flatMap((messages) => messages);
}
